// Derived from the build's base so the SPA works at '/' or under a portal
// path prefix (e.g. /assessment/) with no hardcoded route.
const BASE = `${import.meta.env.BASE_URL.replace(/\/$/, '')}/api/v1`;

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    // The session is an httpOnly cookie, so it must ride along explicitly.
    credentials: 'same-origin',
    headers: body ? { 'content-type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: any;
  try { parsed = JSON.parse(text); } catch { parsed = text; }
  if (!res.ok) {
    const err = new Error(parsed?.detail ?? `Request failed (${res.status})`) as Error & { status: number };
    err.status = res.status;
    throw err;
  }
  return parsed as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

export interface Learner {
  id: string; email: string | null; displayName: string | null; role: string;
  /** Subjects this learner signed up for. */
  packIds?: string[];
}
export interface Cookies {
  total: number;
  byReason: { disputesAccepted: number; contributionsAccepted: number };
  awards: { points: number; reason: string; sourceType: string; sourceId: string; awardedAt: string }[];
}
export interface Dispute {
  id: string; questionId: string; proposedResponse: string[]; comment: string | null;
  state: 'open' | 'accepted' | 'rejected';
  resolutionNote: string | null; resolvedAt: string | null; createdAt: string; stem?: string;
}
export interface Contribution {
  id: string; packId: string; topicName: string | null; stem: string;
  options: { key: string; text: string }[]; correctResponse: string[];
  rationale: string | null; state: 'submitted' | 'accepted' | 'rejected';
  reviewNote: string | null; reviewedAt: string | null; createdAt: string; subject?: string;
}
export interface AdminUser {
  id: string; email: string | null; displayName: string | null; role: string;
  createdAt: string; lastLoginAt: string | null;
  suspended: boolean; suspendedAt: string | null; suspendedReason: string | null;
  subjectCount: number; attemptCount: number; cookies: number;
}
export interface AdminUserDetail extends AdminUser {
  activity: {
    attempts: number; cookies: number;
    disputes: number; disputesAccepted: number;
    contributions: number; contributionsAccepted: number;
  };
  leaderboards: { contributions: boolean; performance: boolean };
  subjects: { id: string; slug: string; subject: string; assignedAt: string }[];
}
/** Per-question performance. Editor-only: the choice counts reveal the key. */
export interface ItemStat {
  packId: string; subject: string; externalId: string;
  stem: string; correctResponse: string[];
  answers: number; correct: number; wrong: number;
  percentCorrect: number; percentWrong: number;
  discrimination: number;
  choiceCounts: Record<string, number>;
  versions: number; keyChanged: boolean; openDisputes: number;
  /** False until enough answers exist for a percentage to mean anything. */
  reportable: boolean;
  /** Every answer so far landed on the same wrong option. */
  unanimousWrong: boolean;
}
export interface ItemAnalysisPayload {
  reportingThreshold: number;
  unanimousMin: number;
  items: ItemStat[];
}
export interface LeaderboardOptIn { contributions: boolean; performance: boolean }
export interface ContributionRank {
  rank: number; name: string; points: number; disputes: number; questions: number;
}
export interface PerformanceRank {
  rank: number; name: string; correct: number; scored: number; attempts: number; accuracy: number;
}

/** A subject offered at sign-up. The only content shape visible before login. */
export interface Subject {
  id: string; slug: string; subject: string; owner: string; itemCount: number;
}

export interface TopicStat {
  topicId: string; topicName: string; answered: number; correct: number;
  incorrect: number; accuracy: number; attempts: number; lastAnsweredAt: string | null;
}
export interface FocusTopic {
  topicId: string; topicName: string; answered: number; correct: number;
  incorrect: number; accuracy: number; reason: string; basis: 'this_session' | 'across_attempts';
}
export interface SubjectProgress {
  packId: string; subject: string; answered: number; correct: number; incorrect: number;
  accuracy: number; attempts: number; completedAttempts: number; lastAttemptAt: string | null;
  topics: TopicStat[]; focusTopics: FocusTopic[];
}
export interface ProgressSummary {
  subjects: SubjectProgress[]; totals: { answered: number; correct: number };
}
export interface AttemptHistoryRow {
  attemptId: string; mode: string; state: string; subject: string; edition: string;
  questionCount: number; startedAt: string; submittedAt: string | null;
  rawScore: number | null; denominator: number | null; accuracy: number | null;
}
export interface MissedQuestion {
  questionId: string; externalId: string; stem: string; subject: string;
  selectedResponse: string[]; correctResponse: string[];
  rationale: string | null; answeredAt: string;
}

export interface Bank {
  bankVersionId: string; bankId: string; packId: string; subject: string; title: string;
  owner: string; edition: string; publishedAt: string; licenceNote: string | null;
  itemCount: number; supportedTypes: string[]; independenceStatement: string;
}
export interface Topic { id: string; name: string; parentId: string | null; itemCount: number }
export interface Preset {
  id: string; name: string; itemCount: number | null; durationSeconds: number | null;
  modes: string[]; feedbackPolicy: string; timerMilestones: number[]; simulationLabelRequired: boolean;
  /** Domains this preset draws from; null means the whole bank. */
  topicFilter: string[] | null;
  /** Present when the preset mirrors a real examination's structure. */
  sectionWeighting: {
    sections?: { questionCount: number; durationSeconds: number }[];
    topicWeights?: Record<string, number>;
  } | null;
  /** Answers may change until their section closes, as in the real exam.
   *  Only ever true with on_submit feedback. */
  answersRevisable: boolean;
}
export interface Pack {
  id: string; slug: string; subject: string; owner: string;
  requiresRationale: boolean; presets: Preset[];
}
export interface Preview {
  eligibleUniqueCount: number; estimatedDurationSeconds: number; unratedCount: number;
  perTopic: { topicId: string; topicName: string; available: number }[];
  shortfall: { requested: number; available: number; remedies: string[] } | null;
}
export interface Question {
  id: string; externalId: string; stem: string; type: string;
  options: { key: string; text: string }[]; topicNames: string[]; difficulty: string;
  correctResponse?: string[]; rationale?: string | null; sourceReference?: string | null;
}
export interface ResponseState {
  questionId: string; revisionNumber: number; selectedResponse: string[] | null;
  flagged: boolean;
  /** True once an answer is recorded in a NON-revisable attempt: it can never
   *  change. Always false in a revisable attempt — there, whether an answer can
   *  still change depends on its section (and the attempt) being open, so a
   *  client must also check section state and attempt state. */
  locked: boolean;
  acknowledgedAt: string | null;
}
export interface AttemptSection {
  sectionIndex: number; questionCount: number; durationSeconds: number;
  /** Inclusive slice of questionOrder this section covers. */
  firstIndex: number; lastIndex: number;
  startedAt: string | null; deadlineAt: string | null; endedAt: string | null;
  remainingSeconds: number | null;
  state: 'not_started' | 'open' | 'ended';
}
export interface Attempt {
  id: string; bankVersionId: string; mode: 'study' | 'timed_mock'; state: string;
  questionOrder: string[]; startedAt: string; deadlineAt: string | null;
  remainingSeconds: number | null; serverTime: string;
  settings: any; allocation: any; responses: ResponseState[];
  /** Null unless the attempt is delivered in separately-timed sections. */
  sections: AttemptSection[] | null;
}
export interface Result {
  attemptId: string; revision: number; rawScore: number; denominator: number;
  exclusions: { questionId: string; reason: string }[];
  perTopic: { topicId: string; topicName: string; correct: number; scored: number }[];
  completedAt: string;
}
export interface ReviewPayload extends Result {
  signedIn: boolean;
  focusTopics: { session: FocusTopic[]; lifetime: FocusTopic[] };
  disclosure: { bankEdition: string | null; contentOwner: string | null; independenceStatement: string; note: string };
  items: {
    questionId: string; stem: string; options: { key: string; text: string }[];
    topicNames: string[]; selectedResponse: string[] | null; correctResponse: string[];
    correct: boolean; answered: boolean; flagged: boolean;
    rationale: string | null; sourceReference: string | null;
    /** Accepted further reading; only ever present once the key is visible. */
    furtherReading: ReadingLink[];
  }[];
}

/** A trusted page explaining the concept behind a correct answer. Not the question's source. */
export interface ReadingLink { url: string; title: string; publisher: string }

export interface StudyPlanQuestion {
  questionId: string; externalId: string; stem: string;
  options: { key: string; text: string }[];
  selectedResponse: string[]; correctResponse: string[];
  rationale: string | null; answeredAt: string;
  furtherReading: ReadingLink[];
}
export interface StudyPlanTopic {
  topicId: string; topicName: string;
  answered: number; correct: number; incorrect: number; accuracy: number;
  needsWork: boolean; stillWrongCount: number; stillWrong: StudyPlanQuestion[];
}
export interface StudyPlan {
  note: string;
  subjects: {
    packId: string; subject: string; bankVersionId: string | null;
    answered: number; correct: number;
    disclosure: { bankEdition: string; contentOwner: string; publishedAt: string;
                  independenceStatement: string } | null;
    ranked: StudyPlanTopic[]; emerging: StudyPlanTopic[];
  }[];
}

// ------------------------------------------------------------------ admin
//
// Shapes returned to editors and administrators only. Every one of these is
// behind a role check on the server; the Admin screen hides the entrance, it
// does not guard the data.

/**
 * Who used the system in a window, and how much. Administrators only.
 * Counts and durations from server timestamps — never a score or an answer.
 */
export interface UsageRow {
  id: string; email: string | null; displayName: string | null; role: string;
  suspended: boolean; lastLoginAt: string | null;
  /** Time inside tests; each pause between recorded events counts up to `idleCapMinutes`. */
  minutesInTests: number;
  attempts: number; submitted: number; answers: number;
  firstActivityAt: string | null; lastActivityAt: string | null;
}
export interface UsageReport {
  from: string; to: string; idleCapMinutes: number;
  totals: { activeUsers: number; accounts: number; minutesInTests: number; attempts: number; answers: number };
  users: UsageRow[];
}

/** An open password reset, waiting for a code or holding a live one. */
export interface ResetRequest {
  id: string; state: 'pending' | 'issued';
  email: string | null; displayName: string | null;
  requestedAt: string; issuedAt: string | null; expiresAt: string | null;
}
/** Returned once, at the moment of issue, and never retrievable again. */
export interface IssuedCode { code: string; expiresAt: string }

export interface AdminDispute extends Dispute {
  externalId: string;
  options: { key: string; text: string }[];
  /** The key as published, against which the reporter's proposal is judged. */
  currentCorrect: string[];
  raisedBy: string | null;
}
export interface AdminContribution extends Contribution {
  submittedBy: string | null;
}

/** A drafted or reviewed further-reading link, for editors. */
export interface AdminReadingLink {
  id: string; url: string; title: string; publisher: string; supports: string;
  alignment: 'aligned' | 'conflict'; note: string; state: 'draft' | 'accepted' | 'rejected';
  draftedBy: string; reviewerName: string | null; reviewedAt: string | null; accessedOn: string | null;
  externalId: string; stem: string; options: { key: string; text: string }[];
  correctResponse: string[]; rationale: string | null; topicNames: string[];
}
export interface ReadingQueue {
  state: 'draft' | 'accepted' | 'rejected';
  /** Newest draft at load time; bulk acceptance is bounded by it. */
  asOf: string | null;
  topics: { topicId: string; topicName: string; packId: string; subject: string;
            drafts: number; conflicts: number; accepted: number }[];
  links: AdminReadingLink[];
}
